import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'next/router';

import { Auth0Provider, OAuthError, useAuth0 } from '@auth0/auth0-react';
import {
  CENTRALIZED_LOGIN_CALLBACK_PATH,
  clearCentralizedSession,
  getCentralizedSessionExpiryDelay,
  getStoredCentralizedSession,
  getRuntimeEnv,
  redirectToCentralizedLogin,
  redirectToCentralizedLogout,
} from '@explorer/helpers';
import { useFeatureFlag } from '@explorer/hooks';
import { appConfig } from '@explorer/src';

import { useDatadogRum } from '@crucible-risk/react-monitoring';

import { useUser } from '../UserStore';

export const useAuth = () => {
  const auth0 = useAuth0();
  const { isAuthenticated, isLoading, error } = auth0;

  const { addError, addAction } = useDatadogRum();
  const {
    setUser,
    resetUser,
    setUserAuthenticated,
    userAuthenticated,
  } = useUser();
  const {
    value: centralizedLoginEnabled = false,
    initialized: centralizedLoginFlagInitialized,
    error: centralizedLoginFlagError,
  } = useFeatureFlag<boolean>('explorer.centralized-login');
  const centralizedLoginFlagReady =
    centralizedLoginFlagInitialized || !!centralizedLoginFlagError;

  const router = useRouter();
  const storedCentralizedSession = getStoredCentralizedSession();
  const hasStoredCentralizedSession = !!storedCentralizedSession;

  useEffect(() => {
    if (!storedCentralizedSession) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      clearCentralizedSession();
      setUserAuthenticated(false);
      resetUser();
    }, getCentralizedSessionExpiryDelay(storedCentralizedSession.expires_at));

    return () => window.clearTimeout(timeout);
  }, [resetUser, setUserAuthenticated, storedCentralizedSession]);

  useEffect(() => {
    if (!userAuthenticated && storedCentralizedSession) {
      setUser(storedCentralizedSession);
      setUserAuthenticated(true);
    }
  }, [
    setUser,
    setUserAuthenticated,
    storedCentralizedSession,
    userAuthenticated,
  ]);

  /**
   * Logout
   *
   * Cleanup the access token and end the upstream session, either via the
   * Coalition Auth Service (Hydra) or Auth0.
   */
  const logout = useCallback(() => {
    setUserAuthenticated(false);
    global.localStorage.clear();
    resetUser();

    if (centralizedLoginEnabled) {
      redirectToCentralizedLogout();
      return;
    }

    auth0.logout({ returnTo: getRuntimeEnv('APP_DOMAIN') });
  }, [auth0, centralizedLoginEnabled, resetUser, setUserAuthenticated]);

  const onTokenRetrievalSuccess = useCallback(
    ({ access_token, id_token }) => {
      setUser({ access_token, id_token });
      setUserAuthenticated(true);
      global.localStorage.setItem('bearer', access_token);
    },
    [setUser, setUserAuthenticated],
  );

  /**
   * `router.query` is empty until the router is ready, so the callback route is
   * matched on pathname too. Without it a redirect would be started before the
   * code is visible, burning the code and bouncing off Hydra in a loop.
   */
  const isHandlingRedirect = useMemo(() => {
    return (
      router.pathname === CENTRALIZED_LOGIN_CALLBACK_PATH ||
      !!router.query?.code ||
      !!router.query?.state
    );
  }, [router.pathname, router.query?.code, router.query?.state]);

  useEffect(() => {
    if (
      isLoading ||
      !router.isReady ||
      !centralizedLoginFlagReady ||
      isHandlingRedirect ||
      hasStoredCentralizedSession ||
      userAuthenticated
    ) {
      /* Wait for auth and the anonymous LaunchDarkly flag to initialize. */
      return;
    }

    if (centralizedLoginEnabled && !isAuthenticated) {
      redirectToCentralizedLogin(window.location.href).catch((e) => {
        const redirectError = new Error(
          'Failed to redirect to Coalition Auth Service',
        );
        redirectError.cause = e;
        addError(redirectError);
      });
      return;
    }

    if (error) {
      const wrappedError = new Error('Auth0 error');
      wrappedError.cause = wrappedError;

      addError(wrappedError);
      return;
    }

    if (isAuthenticated) {
      /* If Auth0 claims we are authenticated, we should be able to retrieve the token from cache. */
      auth0
        .getAccessTokenSilently({
          ignoreCache: false,
          detailedResponse: true,
        })
        .then(onTokenRetrievalSuccess)
        .catch((e) => {
          /* If we fail to retrieve the token while already authenticated, we've run into an edge case with has been observed in the logs. Trigger a redirect login. */
          addAction(
            'Failed to retrieve token while authenticated. Using redirect fallback',
            {
              silentLoginError: e,
              errorCode: (e as OAuthError).error,
            },
          );

          const url = new URL(window.location.href);
          url.searchParams.delete('code');
          url.searchParams.delete('state');

          // Otherwise, trigger a redirect within the browser to Auth0
          return auth0.loginWithRedirect({
            redirectUri: getRuntimeEnv('APP_DOMAIN'),
            appState: {
              returnTo: url.pathname + url.search,
            },
          });
        });

      return;
    }

    /* If we are not authenticated, we should try to silently retrieve our access token. This method uses iframes + 3rd party cross domain cookies, and will fail in browsers with higher privacy settings */
    auth0
      .getAccessTokenSilently({
        ignoreCache: true,
        detailedResponse: true,
      })
      .then(onTokenRetrievalSuccess)

      /* If we do fail to authenticate silently, and will need to use login redirect. This is an expected error, but we can log occurrences to better understand this flow */
      .catch((e) => {
        addAction('Using Auth0 redirect fallback', { silentLoginError: e });

        // Prepare the redirect url after login is successful. Ensure that we don't have code or state query params in that redirect url. Otherwise our app logic will think that we are in the middle of a login flow and enter into an infinite loop.
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');

        // Otherwise, trigger a redirect within the browser to Auth0
        return auth0.loginWithRedirect({
          redirectUri: getRuntimeEnv('APP_DOMAIN'),
          appState: {
            returnTo: url.pathname + url.search,
          },
        });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    centralizedLoginEnabled,
    centralizedLoginFlagReady,
    error,
    hasStoredCentralizedSession,
    isAuthenticated,
    isHandlingRedirect,
    isLoading,
    router.isReady,
    userAuthenticated,
  ]);

  return useMemo(
    () => ({
      logout,
      isLoading:
        isLoading ||
        !centralizedLoginFlagReady ||
        !router.isReady ||
        isHandlingRedirect,
      isAuthenticated: isAuthenticated || userAuthenticated,
    }),
    [
      logout,
      isLoading,
      centralizedLoginFlagReady,
      router.isReady,
      isHandlingRedirect,
      isAuthenticated,
      userAuthenticated,
    ],
  );
};

/**
 * AuthProvider
 */
export const AuthProvider = memo(({ children }: AuthProviderProps) => {
  const { AUTH_0 } = appConfig;
  const router = useRouter();

  return (
    <Auth0Provider
      clientId={AUTH_0?.clientId ?? ''}
      domain={AUTH_0?.domain ?? ''}
      cacheLocation="localstorage"
      redirectUri={getRuntimeEnv('APP_DOMAIN')}
      onRedirectCallback={(appState) => {
        router.replace(appState?.returnTo ?? '/');
      }}
    >
      {children}
    </Auth0Provider>
  );
});

AuthProvider.displayName = 'AuthProvider';

/**
 * AuthProviderProps
 */
interface AuthProviderProps {
  readonly children: JSX.Element | null;
}
